import { useState, useEffect, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { getProjectUsers, getProjectUserRoles, addUserToProject, updateUserInProject, deleteUserFromProject } from '../api/projects';
import { useFeatureFlag } from './useFeatureFlag';

export const useProjectUsers = (projectId: string, archived: boolean = false) => {
  const { keycloak: { token } } = useKeycloak();
  const { isFeatureEnabled } = useFeatureFlag();
  const [users, setUsers] = useState<ProjectUserWithRole[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadUsers = async () => {
    if (archived || !token || !projectId) return;
    
    try {
      setLoading(true);
      setError(null);
      const usersResponse = await getProjectUsers(token, projectId);
      const safeUsers = Array.isArray(usersResponse?.results) ? usersResponse.results : [];
      console.log('useProjectUsers: loaded users', safeUsers);
      setUsers(safeUsers);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    if (archived || !token || !projectId) return;
    try {
      const rolesData: any = await getProjectUserRoles(token, projectId);
      let safeRoles: any[] = [];
      if (
        typeof rolesData === 'object' &&
        !Array.isArray(rolesData) &&
        Array.isArray((rolesData as any).results)
      ) {
        safeRoles = (rolesData as any).results;
      } else if (Array.isArray(rolesData)) {
        safeRoles = rolesData;
      }

      console.log('useProjectUsers: loaded roles', safeRoles);
      
      // Filter out Developer roles in production
      const filteredRoles = isFeatureEnabled('SHOW_DEVELOPER_ROLES') 
        ? safeRoles 
        : safeRoles.filter(role => !role.name?.toLowerCase().includes('developer'));
      
      setRoles(filteredRoles);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const addUser = async (userId: string, userData: any) => {
    if (!token || !projectId) return;
    
    try {
      setError(null); // keep page-level error clean for modal handling
      const newUser = await addUserToProject(token, projectId, userId, userData);
      console.log('useProjectUsers: user added successfully', newUser);
      
      // Force reload the entire user list to ensure consistency
      await loadUsers();
      
      return newUser;
    } catch (err) {
      console.error('useProjectUsers: failed to add user', err);
      throw err;
    }
  };

  const updateUser = async (userId: string, userData: any) => {
    if (!token || !projectId) return;
    try {
      console.log('🔄 Updating user:', userId, 'with data:', userData);
      const updatedUser = await updateUserInProject(token, projectId, userId, userData);
      console.log('✅ User update response:', updatedUser);
      
      if (updatedUser) {
        // Update local state with the response from API
        setUsers(prev => prev.map(user => user.user_id === userId ? updatedUser : user));
      } else {
        // Fallback: update with provided data if no response
        setUsers(prev => prev.map(user =>
          user.user_id === userId
            ? { ...user, ...userData }
            : user
        ));
      }
      
      // Force reload users to ensure data consistency
      console.log('🔄 Force reloading users after update...');
      setTimeout(() => {
        loadUsers();
      }, 500);
      
      return updatedUser;
    } catch (err) {
      console.error('❌ Failed to update user:', err);
      setError(err instanceof Error ? err.message : 'Failed to update user');
      throw err;
    }
  };

  const removeUser = async (userId: string) => {
    if (!token || !projectId) return;
    
    try {
      await deleteUserFromProject(token, projectId, userId);
      setUsers(prev => prev.filter(user => user.user_id !== userId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove user');
      throw err;
    }
  };

  // Guard against React StrictMode's dev-only double-invoke firing this load
  // twice on mount, while still reloading on a genuine projectId change (this
  // hook is used at the top of Project.tsx, which does not remount when
  // switching projects). Keyed only on projectId, not token: getProjectUsers/
  // getProjectUserRoles take an unused `_token` no-op param (auth is handled
  // internally by authenticatedFetch), so keying on token too would
  // spuriously re-fetch every time it rotates.
  const lastLoadedProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!projectId || !token || lastLoadedProjectIdRef.current === projectId) return;
    lastLoadedProjectIdRef.current = projectId;
    loadUsers();
    loadRoles();
  }, [projectId, token]);

  return {
    users: Array.isArray(users) ? users : [],
    roles: Array.isArray(roles) ? roles : [],
    loading,
    error,
    loadUsers,
    addUser,
    updateUser,
    removeUser
  };
}; 